import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/api";
import { useRealtime } from "@/hooks/useRealtime";
import { confirmDialog } from "@/components/Confirm";
import { toast } from "sonner";
import { ListChecks, Plus, Trash2, ChevronLeft, ChevronRight, Repeat, Fuel, StickyNote, ClipboardList, ArrowLeft, X } from "lucide-react";

const todayStr = () => new Date().toLocaleDateString("en-CA");
const prettyDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const KIND_META = {
  rapid_refill: { label: "Rapid Refuel", icon: Fuel, badge: "bg-sky-100 text-sky-700" },
  task: { label: "Taak", icon: ClipboardList, badge: "bg-blue-100 text-blue-700" },
  note: { label: "Notitie", icon: StickyNote, badge: "bg-amber-100 text-amber-700" },
};

// Auto-lerende dropdown: onthoudt eerdere invoer, vrij typen mag, items te verwijderen.
function AutoLearnSelect({ value, onChange, options, onDelete, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = options.filter((o) => !value || o.name.toLowerCase().includes(value.toLowerCase()));
  return (
    <div className="relative" ref={wrapRef}>
      <input data-testid={testid} value={value} onChange={(e) => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        placeholder={placeholder} autoComplete="off"
        className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
      {open && options.length > 0 && (
        <div data-testid={`${testid}-menu`} className="absolute left-0 right-0 top-full z-20 mt-1 max-h-52 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
          {filtered.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Geen opgeslagen opties — typ een nieuwe.</div>}
          {filtered.map((o) => (
            <div key={o.id} className="group flex items-center justify-between hover:bg-blue-50">
              <button type="button" data-testid={`${testid}-opt`} onMouseDown={(e) => { e.preventDefault(); onChange(o.name); setOpen(false); }}
                className="flex-1 truncate px-3 py-2 text-left text-sm text-slate-700">{o.name}</button>
              <button type="button" title="Uit menu verwijderen" data-testid={`${testid}-del`} onMouseDown={(e) => { e.preventDefault(); onDelete(o.id); }}
                className="mr-1 rounded-md p-1 text-slate-300 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Tasks() {
  const [date, setDate] = useState(todayStr());
  const [tasks, setTasks] = useState([]);
  const [locations, setLocations] = useState([]);

  // Wizard
  const [step, setStep] = useState("type"); // type | form
  const [kind, setKind] = useState(null);
  const [saving, setSaving] = useState(false);

  // Taak-velden
  const [title, setTitle] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loc, setLoc] = useState("");
  const [sugs, setSugs] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [recur, setRecur] = useState(false);
  const [freq, setFreq] = useState("daily");
  const [endMode, setEndMode] = useState("count");
  const [count, setCount] = useState(5);
  const [until, setUntil] = useState("");

  // Rapid refill-velden
  const [rfDate, setRfDate] = useState(todayStr());
  const [rfTime, setRfTime] = useState("");
  const [rfVehicle, setRfVehicle] = useState("");
  const [rfAircraft, setRfAircraft] = useState("");
  const [tankers, setTankers] = useState([]);
  const [aircrafts, setAircrafts] = useState([]);

  // Notitie-velden
  const [noteDate, setNoteDate] = useState(todayStr());
  const [noteText, setNoteText] = useState("");

  const load = useCallback(() => { api.get(`/tasks?date=${date}`).then(({ data }) => setTasks(data || [])).catch(() => {}); }, [date]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/locations").then(({ data }) => setLocations(data || [])).catch(() => {}); }, []);
  const loadLearned = useCallback(() => {
    api.get("/refill-vehicles?category=tanker").then(({ data }) => setTankers(data || [])).catch(() => {});
    api.get("/refill-vehicles?category=aircraft").then(({ data }) => setAircrafts(data || [])).catch(() => {});
  }, []);
  useEffect(() => { loadLearned(); }, [loadLearned]);
  const locName = (id) => (locations.find((l) => l.id === id) || {}).name;
  const fetchSugs = (v) => { api.get(`/tasks/suggestions?q=${encodeURIComponent(v || "")}`).then(({ data }) => { setSugs(data || []); setShowSug(true); }).catch(() => {}); };
  useRealtime((m) => { if (m && m.type === "tasks_updated") load(); });

  const shiftDay = (n) => { const d = new Date(`${date}T00:00:00`); d.setDate(d.getDate() + n); setDate(d.toLocaleDateString("en-CA")); };

  const resetWizard = () => {
    setStep("type"); setKind(null);
    setTitle(""); setStart(""); setEnd(""); setRecur(false);
    setRfTime(""); setRfVehicle(""); setRfAircraft("");
    setNoteText("");
  };
  const pickKind = (k) => {
    setKind(k); setStep("form");
    if (k === "rapid_refill") { setRfDate(date); loadLearned(); }
    if (k === "note") setNoteDate(date);
  };

  const deleteLearned = async (id) => { try { await api.delete(`/refill-vehicles/${id}`); loadLearned(); } catch (e) {} };

  const save = async () => {
    setSaving(true);
    try {
      if (kind === "task") {
        if (!title.trim()) { toast.error("Geef een omschrijving op"); setSaving(false); return; }
        const body = { kind: "task", title: title.trim(), date, start_time: start || null, end_time: end || null, location_id: loc || null };
        if (recur) body.recurrence = { frequency: freq, count: endMode === "count" ? Number(count) : null, until: endMode === "until" ? until : null };
        const { data } = await api.post("/tasks", body);
        toast.success(recur ? `${data.count} taken toegevoegd` : "Taak toegevoegd");
      } else if (kind === "rapid_refill") {
        if (!rfVehicle.trim() && !rfAircraft.trim()) { toast.error("Vul minstens voertuig of helikopter/vliegtuig in"); setSaving(false); return; }
        await api.post("/tasks", { kind: "rapid_refill", date: rfDate, start_time: rfTime || null, vehicle: rfVehicle.trim() || null, aircraft: rfAircraft.trim() || null, location_id: loc || null });
        toast.success("Rapid Refuel toegevoegd");
        loadLearned();
      } else if (kind === "note") {
        if (!noteText.trim()) { toast.error("Geef een notitietekst op"); setSaving(false); return; }
        await api.post("/tasks", { kind: "note", title: noteText.trim(), date: noteDate, location_id: loc || null });
        toast.success("Notitie toegevoegd");
      }
      if (kind === "rapid_refill") setDate(rfDate);
      else if (kind === "note") setDate(noteDate);
      resetWizard();
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggle = (t) => { api.patch(`/tasks/${t.id}`, { done: !t.done }).then(() => setTasks((ts) => ts.map((x) => x.id === t.id ? { ...x, done: !x.done } : x))).catch(() => {}); };
  const del = async (t) => {
    let scope = "one";
    if (t.series_id) { scope = (await confirmDialog({ title: "Terugkerende taak", message: "Alleen deze dag verwijderen, of de hele reeks?", confirmText: "Hele reeks", cancelText: "Alleen deze dag" })) ? "series" : "one"; }
    else if (!(await confirmDialog({ title: "Verwijderen", message: `"${t.title}" verwijderen?`, danger: true, confirmText: "Verwijderen" }))) return;
    try { await api.delete(`/tasks/${t.id}?scope=${scope}`); load(); } catch (e) {}
  };

  const refills = tasks.filter((t) => t.kind === "rapid_refill");
  const notes = tasks.filter((t) => t.kind === "note");
  const plainTasks = tasks.filter((t) => (t.kind || "task") === "task");

  const Row = ({ t }) => {
    const meta = KIND_META[t.kind || "task"];
    const Icon = meta.icon;
    return (
      <div data-testid={`task-item-${t.id}`} className={`flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-sm ${t.done ? "border-slate-100 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
        {(t.kind || "task") === "task" ? (
          <button data-testid={`task-toggle-${t.id}`} onClick={() => toggle(t)} className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 text-sm ${t.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 text-transparent hover:border-blue-500"}`}>✓</button>
        ) : (
          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${meta.badge}`}><Icon className="h-4 w-4" /></span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span data-testid={`task-kind-${t.id}`} className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.badge}`}>{meta.label}</span>
            <span className={`truncate text-sm font-semibold ${t.done ? "text-slate-500 line-through" : "text-slate-900"}`}>
              {t.kind === "rapid_refill" ? (t.aircraft ? `${t.aircraft}` : t.title) : t.title}
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            {t.start_time && <span>{t.start_time}{t.end_time ? ` – ${t.end_time}` : ""}</span>}
            {t.kind === "rapid_refill" && t.vehicle && <span className="inline-flex items-center gap-0.5 rounded bg-sky-50 px-1.5 py-0.5 font-medium text-sky-700"><Fuel className="h-3 w-3" /> {t.vehicle}</span>}
            {t.location_id && <span className="inline-flex items-center gap-0.5 rounded bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700">{locName(t.location_id) || "Kazerne"}</span>}
            {t.series_id && <span className="inline-flex items-center gap-0.5 text-blue-600"><Repeat className="h-3 w-3" /> reeks</span>}
          </div>
        </div>
        <button data-testid={`task-del-${t.id}`} onClick={() => del(t)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      </div>
    );
  };

  const Group = ({ title: gtitle, items, Icon }) => items.length === 0 ? null : (
    <div className="space-y-2" data-testid={`task-group-${gtitle.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Icon className="h-3.5 w-3.5" /> {gtitle} ({items.length})</div>
      {items.map((t) => <Row key={t.id} t={t} />)}
    </div>
  );

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="tasks-page">
      <div className="flex items-center gap-2">
        <ListChecks className="h-6 w-6 text-blue-600" />
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Takenlijst</h1>
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <button data-testid="task-prev-day" onClick={() => shiftDay(-1)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><ChevronLeft className="h-5 w-5" /></button>
        <div className="text-center">
          <div className="text-sm font-bold capitalize text-slate-900">{prettyDate(date)}</div>
          <input type="date" data-testid="task-date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-500" />
        </div>
        <button data-testid="task-next-day" onClick={() => shiftDay(1)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"><ChevronRight className="h-5 w-5" /></button>
      </div>

      {/* Wizard */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="task-wizard">
        {step === "type" ? (
          <>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Nieuw toevoegen — wat wil je doen?</div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <button data-testid="wizard-pick-task" onClick={() => pickKind("task")} className="flex flex-col items-start gap-2 rounded-2xl border border-slate-200 p-4 text-left transition-all hover:border-blue-400 hover:bg-blue-50">
                <span className="rounded-lg bg-blue-100 p-2 text-blue-700"><ClipboardList className="h-5 w-5" /></span>
                <span className="font-semibold text-slate-900">Nieuwe taak</span>
                <span className="text-xs text-slate-500">Taak met tijd, kazerne en optioneel terugkerend.</span>
              </button>
              <button data-testid="wizard-pick-refill" onClick={() => pickKind("rapid_refill")} className="flex flex-col items-start gap-2 rounded-2xl border border-slate-200 p-4 text-left transition-all hover:border-sky-400 hover:bg-sky-50">
                <span className="rounded-lg bg-sky-100 p-2 text-sky-700"><Fuel className="h-5 w-5" /></span>
                <span className="font-semibold text-slate-900">Rapid Refuel</span>
                <span className="text-xs text-slate-500">Datum, tijd, aftankend voertuig en helikopter/vliegtuig.</span>
              </button>
              <button data-testid="wizard-pick-note" onClick={() => pickKind("note")} className="flex flex-col items-start gap-2 rounded-2xl border border-slate-200 p-4 text-left transition-all hover:border-amber-400 hover:bg-amber-50">
                <span className="rounded-lg bg-amber-100 p-2 text-amber-700"><StickyNote className="h-5 w-5" /></span>
                <span className="font-semibold text-slate-900">Notitie</span>
                <span className="text-xs text-slate-500">Tekst op een bepaalde dag; verdwijnt na die dag.</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-between">
              <button data-testid="wizard-back" onClick={resetWizard} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Terug</button>
              <span className={`rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${KIND_META[kind].badge}`}>{KIND_META[kind].label}</span>
            </div>

            {kind === "task" && (
              <>
                <div className="relative mb-2">
                  <input data-testid="task-title" value={title} onChange={(e) => { setTitle(e.target.value); fetchSugs(e.target.value); }} onFocus={() => fetchSugs(title)} onBlur={() => setTimeout(() => setShowSug(false), 150)} onKeyDown={(e) => { if (e.key === "Enter" && !recur) save(); }}
                    placeholder="Omschrijving van de taak" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" autoComplete="off" />
                  {showSug && sugs.length > 0 && (
                    <div data-testid="task-suggestions" className="absolute left-0 right-0 top-full z-20 mt-1 max-h-52 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                      {sugs.map((s) => (
                        <button key={s} type="button" data-testid="task-suggestion" onMouseDown={(e) => { e.preventDefault(); setTitle(s); setShowSug(false); }}
                          className="block w-full truncate px-3 py-2 text-left text-sm text-slate-700 hover:bg-blue-50">{s}</button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mb-2 flex flex-wrap items-center gap-3 text-sm">
                  <label className="flex items-center gap-1.5 text-slate-600">Aanvang <input type="time" data-testid="task-start" value={start} onChange={(e) => setStart(e.target.value)} className="rounded border border-slate-200 px-2 py-1" /></label>
                  <label className="flex items-center gap-1.5 text-slate-600">Einde <input type="time" data-testid="task-end" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded border border-slate-200 px-2 py-1" /></label>
                  <label className="flex items-center gap-1.5 text-slate-600">Kazerne <select data-testid="task-location" value={loc} onChange={(e) => setLoc(e.target.value)} className="rounded border border-slate-200 px-2 py-1"><option value="">Alle</option>{locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></label>
                  <label className="ml-auto flex cursor-pointer items-center gap-1.5 font-semibold text-slate-700"><input type="checkbox" data-testid="task-recur" checked={recur} onChange={(e) => setRecur(e.target.checked)} className="h-4 w-4 accent-blue-600" /><Repeat className="h-4 w-4" /> Terugkerend</label>
                </div>
                {recur && (
                  <div className="mb-2 flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 p-3 text-sm" data-testid="task-recur-opts">
                    <select data-testid="task-freq" value={freq} onChange={(e) => setFreq(e.target.value)} className="rounded border border-slate-200 px-2 py-1">
                      <option value="daily">Dagelijks</option><option value="weekly">Wekelijks</option><option value="biweekly">Tweewekelijks</option><option value="monthly">Maandelijks</option>
                    </select>
                    <label className="flex items-center gap-1.5"><input type="radio" name="endmode" checked={endMode === "count"} onChange={() => setEndMode("count")} /> Aantal keer
                      <input type="number" min="1" data-testid="task-count" value={count} onChange={(e) => setCount(e.target.value)} disabled={endMode !== "count"} className="w-16 rounded border border-slate-200 px-2 py-1 disabled:opacity-50" /></label>
                    <label className="flex items-center gap-1.5"><input type="radio" name="endmode" checked={endMode === "until"} onChange={() => setEndMode("until")} /> Tot datum
                      <input type="date" data-testid="task-until" value={until} onChange={(e) => setUntil(e.target.value)} disabled={endMode !== "until"} className="rounded border border-slate-200 px-2 py-1 disabled:opacity-50" /></label>
                  </div>
                )}
              </>
            )}

            {kind === "rapid_refill" && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <label className="flex items-center gap-1.5 text-slate-600">Datum <input type="date" data-testid="refill-date" value={rfDate} onChange={(e) => setRfDate(e.target.value)} className="rounded border border-slate-200 px-2 py-1" /></label>
                  <label className="flex items-center gap-1.5 text-slate-600">Tijd <input type="time" data-testid="refill-time" value={rfTime} onChange={(e) => setRfTime(e.target.value)} className="rounded border border-slate-200 px-2 py-1" /></label>
                  <label className="flex items-center gap-1.5 text-slate-600">Kazerne <select data-testid="refill-location" value={loc} onChange={(e) => setLoc(e.target.value)} className="rounded border border-slate-200 px-2 py-1"><option value="">Alle</option>{locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></label>
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold text-slate-600">Aftankend voertuig</div>
                  <AutoLearnSelect value={rfVehicle} onChange={setRfVehicle} options={tankers} onDelete={deleteLearned} placeholder="Bijv. Tankwagen TW-1" testid="refill-vehicle" />
                </div>
                <div>
                  <div className="mb-1 text-xs font-semibold text-slate-600">Helikopter / vliegtuig (wordt getankt)</div>
                  <AutoLearnSelect value={rfAircraft} onChange={setRfAircraft} options={aircrafts} onDelete={deleteLearned} placeholder="Bijv. Heli PH-ABC" testid="refill-aircraft" />
                </div>
              </div>
            )}

            {kind === "note" && (
              <div className="space-y-3">
                <label className="flex items-center gap-1.5 text-sm text-slate-600">Datum <input type="date" data-testid="note-date" value={noteDate} onChange={(e) => setNoteDate(e.target.value)} className="rounded border border-slate-200 px-2 py-1" /></label>
                <textarea data-testid="note-text" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} placeholder="Tekst van de notitie" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" />
              </div>
            )}

            <button data-testid="task-add" onClick={save} disabled={saving} className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60"><Plus className="h-4 w-4" /> Toevoegen</button>
          </>
        )}
      </div>

      <div className="space-y-4">
        {tasks.length === 0 && <div className="rounded-2xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">Niets voor deze dag</div>}
        <Group title="Rapid Refuel" items={refills} Icon={Fuel} />
        <Group title="Taken" items={plainTasks} Icon={ClipboardList} />
        <Group title="Notities" items={notes} Icon={StickyNote} />
      </div>
    </div>
  );
}
